import type { EditableLicensePayment } from "@/components/license-details/cards/payments-card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormFieldWrapper } from "@/components/ui/form-field-wrapper";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { useAppForm } from "@/components/ui/tanstack-form";
import { Textarea } from "@/components/ui/textarea";
import {
  createManualPaymentMutation,
  getManualPaymentAvailablePeriodsOptions,
  updateLicensePaymentPeriodMutation,
  updateManualPaymentMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import { notifyLicenseDetailsChanged } from "@/lib/license-details-change-notification";
import {
  formatDate,
  getLocalTodayYmd,
  getQueryErrorMessage,
} from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { z } from "zod";
import { QuestionField } from "../../registrant/form-utils/question-field-utils";

type ManualPaymentDialogVariant = "payment" | "fee_adjustment";

interface ManualPaymentDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  licenseId: string;
  onPaymentAdded?: () => void;
  /** Variant when creating new: "payment" for check/other, "fee_adjustment" for fee adjustments. When editing, derived from editPayment.manual_payment_method_type. */
  variant?: ManualPaymentDialogVariant;
  editPayment?: EditableLicensePayment | null;
  elevated?: boolean;
}

const ManualPaymentSchema = z.object({
  period: z.string().min(1, "Period is required"),
  fee: z
    .string()
    .min(1, "Registration fee is required")
    .regex(/^\-?\d+(\.\d{1,2})?$/, "Must be a valid currency amount"),
  late_fee: z
    .string()
    .regex(/^\-?\d+(\.\d{1,2})?$/, "Must be a valid currency amount"),
  processing_fee: z
    .string()
    .regex(/^\-?\d+(\.\d{1,2})?$/, "Must be a valid currency amount"),
  note: z.string().max(1000, "Payment note must be 1000 characters or less"),
  comment: z.string(),
  manual_payment_date: z.string().min(1, "Payment date is required"),
  manual_payment_method_type: z.string().min(1, "Payment method is required"),
});

const FeeAdjustmentSchema = ManualPaymentSchema.omit({
  manual_payment_method_type: true,
});

const EditPeriodFieldsSchema = {
  period_start_date: z.string().min(1, "Period start date is required"),
  period_end_date: z.string(),
};

const EditManualPaymentSchema = ManualPaymentSchema.omit({
  period: true,
}).extend(EditPeriodFieldsSchema);

const EditFeeAdjustmentSchema = FeeAdjustmentSchema.omit({
  period: true,
}).extend(EditPeriodFieldsSchema);

const EditPeriodOnlySchema = z.object({
  ...EditPeriodFieldsSchema,
  note: z.string().max(1000, "Payment note must be 1000 characters or less"),
});

export function ManualPaymentDialog({
  isOpen,
  onOpenChange,
  licenseId,
  onPaymentAdded,
  variant = "payment",
  editPayment,
  elevated = false,
}: ManualPaymentDialogProps) {
  const queryClient = useQueryClient();
  const [isCommentPublic, setIsCommentPublic] = useState(
    editPayment?.is_comment_public ?? false,
  );
  const isEditMode = !!editPayment;
  const isPeriodOnlyEdit =
    isEditMode &&
    editPayment?.payment_type?.toLowerCase() === "payment_processor";

  // When editing, derive variant from payment type; otherwise use prop
  const effectiveVariant: ManualPaymentDialogVariant =
    isEditMode && editPayment?.manual_payment_method_type === "fee_adjustment"
      ? "fee_adjustment"
      : variant;

  const availablePeriods = useQuery(
    getManualPaymentAvailablePeriodsOptions({
      path: { license_id: licenseId },
    }),
  );

  const createManualPayment = useMutation({
    ...createManualPaymentMutation(),
  });

  const updateManualPayment = useMutation({
    ...updateManualPaymentMutation(),
  });

  const updateLicensePaymentPeriod = useMutation({
    ...updateLicensePaymentPeriodMutation(),
  });

  const getTodayDate = () => getLocalTodayYmd();

  const form = useAppForm({
    defaultValues: {
      period: "",
      period_start_date: editPayment?.period_start_date ?? "",
      period_end_date: editPayment?.period_end_date ?? "",
      fee: editPayment?.fee_paid ?? "",
      late_fee: editPayment?.late_fee_paid ?? "0.00",
      processing_fee:
        effectiveVariant === "fee_adjustment"
          ? "0.00"
          : (editPayment?.processing_fee ?? "0.00"),
      note: editPayment?.note ?? "",
      comment: editPayment?.comment ?? "",
      manual_payment_date: editPayment?.manual_payment_date ?? getTodayDate(),
      manual_payment_method_type:
        effectiveVariant === "payment"
          ? (editPayment?.manual_payment_method_type ?? "check")
          : "fee_adjustment",
    },
    validators: {
      onSubmit: ({ value }) => {
        const schema = isPeriodOnlyEdit
          ? EditPeriodOnlySchema
          : effectiveVariant === "fee_adjustment"
            ? isEditMode
              ? EditFeeAdjustmentSchema
              : FeeAdjustmentSchema
            : isEditMode
              ? EditManualPaymentSchema
              : ManualPaymentSchema;
        schema.parse(value);
        // Return undefined to indicate success (don't return the parsed value)
      },
    },
    onSubmit: async ({ value }) => {
      try {
        if (isEditMode && editPayment) {
          if (isPeriodOnlyEdit) {
            await updateLicensePaymentPeriod.mutateAsync({
              path: { license_id: licenseId, payment_id: editPayment.id },
              body: {
                period_start_date: value.period_start_date,
                period_end_date: value.period_end_date || null,
                note: value.note.trim() || null,
              },
            });
          } else {
            const manualPaymentMethodType =
              effectiveVariant === "fee_adjustment"
                ? "fee_adjustment"
                : ((value.manual_payment_method_type || null) as any);
            await updateManualPayment.mutateAsync({
              path: { license_id: licenseId, payment_id: editPayment.id },
              body: {
                fee_paid: value.fee,
                late_fee_paid: value.late_fee,
                period_start_date: value.period_start_date,
                period_end_date: value.period_end_date || null,
                processing_fee:
                  effectiveVariant === "fee_adjustment"
                    ? "0"
                    : value.processing_fee,
                note: value.note.trim() || null,
                comment: value.comment,
                is_comment_public: isCommentPublic,
                manual_payment_date: value.manual_payment_date || null,
                manual_payment_method_type: manualPaymentMethodType,
              },
            });
          }

          await notifyLicenseDetailsChanged(queryClient, licenseId);
          toast({
            title: "Success",
            description: isPeriodOnlyEdit
              ? "Payment period updated successfully"
              : effectiveVariant === "fee_adjustment"
                ? "Fee adjustment updated successfully"
                : "Payment updated successfully",
            variant: "secondary",
          });

          // Close dialog and reset state
          form.reset();
          setIsCommentPublic(false);
          onPaymentAdded?.();
          onOpenChange(false);
          return;
        }

        // Create new payment
        const selectedPeriod = availablePeriods.data?.periods.find(
          (p) => `${p.start_date}_${p.end_date}` === value.period,
        );

        if (!selectedPeriod) {
          throw new Error("Selected period not found");
        }

        const manualPaymentMethodType =
          effectiveVariant === "fee_adjustment"
            ? "fee_adjustment"
            : ((value.manual_payment_method_type || null) as any);
        const requestBody = {
          period_start_date: selectedPeriod.start_date,
          period_end_date: selectedPeriod.end_date,
          fee_paid: value.fee,
          late_fee_paid: value.late_fee,
          processing_fee:
            effectiveVariant === "fee_adjustment" ? "0" : value.processing_fee,
          note: value.note.trim() || null,
          comment: value.comment,
          is_comment_public: isCommentPublic,
          manual_payment_date: value.manual_payment_date || null,
          manual_payment_method_type: manualPaymentMethodType,
        };

        await createManualPayment.mutateAsync({
          path: { license_id: licenseId },
          body: requestBody,
        });

        await notifyLicenseDetailsChanged(queryClient, licenseId);
        if (isCommentPublic && value.comment.trim()) {
          await queryClient.invalidateQueries({
            queryKey: ["listLicenseNotificationEmails", licenseId],
          });
        }
        toast({
          title: "Success",
          description:
            effectiveVariant === "fee_adjustment"
              ? "Fee adjustment added successfully"
              : "Payment added successfully",
          variant: "secondary",
        });

        form.reset();
        setIsCommentPublic(false);
        onPaymentAdded?.();
        onOpenChange(false);
      } catch (error) {
        toast({
          title: "Error",
          description: getQueryErrorMessage(error),
          variant: "destructive",
        });
      }
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const errors = await form.validateAllFields("submit");
    if (errors.length === 0) {
      await form.handleSubmit();
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.reset();
      setIsCommentPublic(editPayment?.is_comment_public ?? false);
    }
    onOpenChange(open);
  };

  const periodOptions =
    availablePeriods.data?.periods.map((period) => ({
      value: `${period.start_date}_${period.end_date}`,
      label: `${formatDate(period.start_date)} - ${formatDate(period.end_date)}`,
    })) || [];

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent elevated={elevated} className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isPeriodOnlyEdit
              ? "Edit Payment Period"
              : effectiveVariant === "fee_adjustment"
                ? isEditMode
                  ? "Edit Fee Adjustment"
                  : "Add Fee Adjustment"
                : isEditMode
                  ? "Edit Payment"
                  : "Add Payment"}
          </DialogTitle>
          <DialogDescription>
            {isPeriodOnlyEdit
              ? "Change the registration period this payment applies to, and add a note that will appear on its receipt. Charged amounts stay as they were collected. Changing the period can move the renewal date and how remaining fees are calculated."
              : isEditMode
                ? effectiveVariant === "fee_adjustment"
                  ? "Update the details of this fee adjustment."
                  : "Update the details of this payment."
                : effectiveVariant === "fee_adjustment"
                  ? "Adjust the fees due for a period. Fee adjustments change what is owed and are not considered fees collected."
                  : "Record a check or other payment for this registration. It will be visible to the registrant and a HERA fee will be deducted."}
          </DialogDescription>
        </DialogHeader>
        <form.AppForm>
          <form className="space-y-4" onSubmit={handleSubmit}>
            {!isEditMode && (
              <form.AppField
                name="period"
                validators={{
                  onChange: ManualPaymentSchema.shape.period,
                }}
              >
                {(field) => (
                  <FormFieldWrapper field={field} label="Time Period">
                    <QuestionField
                      answerType="single_select"
                      field={field}
                      answerOptions={periodOptions}
                    />
                  </FormFieldWrapper>
                )}
              </form.AppField>
            )}

            {isEditMode && (
              <div className="grid grid-cols-2 gap-4">
                <form.AppField
                  name="period_start_date"
                  validators={{
                    onChange: EditPeriodFieldsSchema.period_start_date,
                  }}
                >
                  {(field) => (
                    <FormFieldWrapper field={field} label="Period Start Date">
                      <QuestionField answerType="date" field={field} />
                    </FormFieldWrapper>
                  )}
                </form.AppField>
                <form.AppField
                  name="period_end_date"
                  validators={{
                    onChange: EditPeriodFieldsSchema.period_end_date,
                  }}
                >
                  {(field) => (
                    <FormFieldWrapper
                      field={field}
                      label="Period End Date (optional)"
                    >
                      <QuestionField answerType="date" field={field} />
                    </FormFieldWrapper>
                  )}
                </form.AppField>
              </div>
            )}

            {isPeriodOnlyEdit && (
              <form.AppField
                name="note"
                validators={{
                  onChange: ManualPaymentSchema.shape.note,
                }}
              >
                {(field) => (
                  <FormFieldWrapper
                    field={field}
                    label="Payment note (optional)"
                  >
                    <div>
                      <Textarea
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        placeholder="For example, a check number or reference"
                        maxLength={1000}
                      />
                      <p className="mt-1 text-xs text-muted-foreground">
                        This note will appear on the payment receipt.
                      </p>
                    </div>
                  </FormFieldWrapper>
                )}
              </form.AppField>
            )}

            {!isPeriodOnlyEdit && (
              <>
                <form.AppField
                  name="manual_payment_date"
                  validators={{
                    onChange: ManualPaymentSchema.shape.manual_payment_date,
                  }}
                >
                  {(field) => (
                    <FormFieldWrapper field={field} label="Payment Date">
                      <QuestionField answerType="date" field={field} />
                    </FormFieldWrapper>
                  )}
                </form.AppField>

                <div className="grid grid-cols-2 gap-4 items-start">
                  <div className="min-h-[80px]">
                    <form.AppField
                      name="fee"
                      validators={{
                        onChange: ManualPaymentSchema.shape.fee,
                      }}
                    >
                      {(field) => (
                        <FormFieldWrapper
                          field={field}
                          label="Registration Fee"
                        >
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                          />
                        </FormFieldWrapper>
                      )}
                    </form.AppField>
                  </div>
                  <div className="min-h-[80px]">
                    <form.AppField
                      name="late_fee"
                      validators={{
                        onChange: ManualPaymentSchema.shape.late_fee,
                      }}
                    >
                      {(field) => (
                        <FormFieldWrapper field={field} label="Late Fee">
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                          />
                        </FormFieldWrapper>
                      )}
                    </form.AppField>
                  </div>
                </div>

                {effectiveVariant === "payment" && (
                  <div className="min-h-[80px]">
                    <form.AppField
                      name="processing_fee"
                      validators={{
                        onChange: ManualPaymentSchema.shape.processing_fee,
                      }}
                    >
                      {(field) => (
                        <FormFieldWrapper field={field} label="Processing Fee">
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                          />
                        </FormFieldWrapper>
                      )}
                    </form.AppField>
                  </div>
                )}

                {effectiveVariant === "payment" && (
                  <form.AppField
                    name="manual_payment_method_type"
                    validators={{
                      onChange:
                        ManualPaymentSchema.shape.manual_payment_method_type,
                    }}
                  >
                    {(field) => (
                      <FormFieldWrapper field={field} label="Payment Method">
                        <div>
                          <QuestionField
                            answerType="single_select"
                            field={field}
                            answerOptions={[
                              { value: "check", label: "Check" },
                              { value: "other", label: "Other" },
                            ]}
                          />
                          <p className="text-xs text-muted-foreground mt-2">
                            Check and Other payments will be treated just like
                            any other payment and a HERA fee will be deducted.
                          </p>
                        </div>
                      </FormFieldWrapper>
                    )}
                  </form.AppField>
                )}

                <form.AppField
                  name="note"
                  validators={{
                    onChange: ManualPaymentSchema.shape.note,
                  }}
                >
                  {(field) => (
                    <FormFieldWrapper
                      field={field}
                      label="Payment note (optional)"
                    >
                      <div>
                        <Textarea
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          placeholder="For example, a check number or reference"
                          maxLength={1000}
                        />
                        <p className="mt-1 text-xs text-muted-foreground">
                          This note will appear on the payment receipt.
                        </p>
                      </div>
                    </FormFieldWrapper>
                  )}
                </form.AppField>

                <form.AppField
                  name="comment"
                  validators={{
                    onChange: ManualPaymentSchema.shape.comment,
                  }}
                >
                  {(field) => (
                    <FormFieldWrapper field={field} label="Optional comment">
                      <Textarea
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        placeholder="Add a note about this payment..."
                      />
                    </FormFieldWrapper>
                  )}
                </form.AppField>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="comment-visibility"
                    checked={isCommentPublic}
                    onCheckedChange={(checked) =>
                      setIsCommentPublic(checked === true)
                    }
                  />
                  <Label htmlFor="comment-visibility" className="text-sm">
                    Make comment visible to registrant
                  </Label>
                </div>
              </>
            )}
          </form>
        </form.AppForm>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={
              createManualPayment.isPending ||
              updateManualPayment.isPending ||
              updateLicensePaymentPeriod.isPending
            }
          >
            Cancel
          </Button>
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
          >
            {([canSubmit, isSubmitting]) => (
              <Button
                onClick={async () => {
                  const errors = await form.validateAllFields("submit");
                  if (errors.length === 0) {
                    await form.handleSubmit();
                  }
                }}
                disabled={
                  !canSubmit ||
                  createManualPayment.isPending ||
                  updateManualPayment.isPending ||
                  updateLicensePaymentPeriod.isPending ||
                  (!isEditMode && availablePeriods.isLoading)
                }
              >
                {isSubmitting ||
                createManualPayment.isPending ||
                updateManualPayment.isPending ||
                updateLicensePaymentPeriod.isPending
                  ? isEditMode
                    ? "Updating..."
                    : "Adding..."
                  : isPeriodOnlyEdit
                    ? "Update Period"
                    : effectiveVariant === "fee_adjustment"
                      ? isEditMode
                        ? "Update Fee Adjustment"
                        : "Add Fee Adjustment"
                      : isEditMode
                        ? "Update Payment"
                        : "Add Payment"}
              </Button>
            )}
          </form.Subscribe>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
